import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { type BarItem, BarList } from "@/components/charts/BarList";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type CompetencyAggregateRow,
  useAllRolesCompetencyOverview,
  useCompetencyAggregateContributors,
  useCompetencyReportCycles,
  useCompetencyReportScope,
  useRoleCompetencyAggregates,
} from "@/hooks/use-competency-report";
import { categoryColorVar } from "@/lib/category-colors";

/** The 1–5 capability scale every rating is on — the overview's bar ceiling. */
const SCALE_MAX = 5;

/**
 * How a whole role performed against each competency — EPIC-071.
 *
 * Every story in this epic is written "as a pillar head". HR/admin open every
 * role; a pillar head (a field on the pillar, EPIC-076 WI-284) opens the roles
 * in their own pillar and sees the same totals. The drill-through names only
 * the people who report to that head, so seeing totals never grants access to
 * individuals — the server narrows it, and the dialog below says so.
 */
export function CompetencyReport() {
  const [roleId, setRoleId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  // `null` is "all levels" — the existing per-level breakdown (WI-317).
  const [levelId, setLevelId] = useState<string | null>(null);
  const [selectedCycleIds, setSelectedCycleIds] = useState<string[]>([]);
  const [drillDown, setDrillDown] = useState<{
    roleLevelId: string | null;
    roleLevelLabel: string;
    competency: CompetencyAggregateRow;
  } | null>(null);

  // Names only, from the report's own route — the full cycles list is HR/admin.
  const { data: closedCycles = [] } = useCompetencyReportCycles();
  const { data: scope } = useCompetencyReportScope();
  const {
    data: groups,
    isLoading,
    isError,
    error,
    refetch,
  } = useRoleCompetencyAggregates(roleId, selectedCycleIds, categoryId, levelId);

  return (
    <div className="space-y-6">
      <DetailSection title="Choose what to report on">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-full max-w-xs">
              <Label className="mb-1.5 block">Role</Label>
              {scope?.mode === "pillar" ? (
                // A pillar head may report on their own pillar's roles only, so
                // the choice is that short list rather than every role.
                <Select value={roleId ?? undefined} onValueChange={(id) => setRoleId(id)}>
                  <SelectTrigger aria-label="Role" className="w-full">
                    <SelectValue placeholder="Choose a role…" />
                  </SelectTrigger>
                  <SelectContent>
                    {scope.roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <EntityCombobox
                  entity="roles"
                  value={roleId}
                  onChange={setRoleId}
                  placeholder="Choose a role…"
                />
              )}
            </div>
            <div className="w-full max-w-xs">
              <Label className="mb-1.5 block">
                Category{" "}
                <span className="font-normal text-muted-foreground">
                  (every one, if none picked)
                </span>
              </Label>
              {/*
                `includeNone` is the way back to every competency, including
                ones carrying no category — WI-318. A category is a lens here,
                never a condition of appearing in the report.
              */}
              <EntityCombobox
                entity="categories"
                value={categoryId}
                onChange={setCategoryId}
                includeNone
                noneLabel="Every category"
                placeholder="Every category"
                searchPlaceholder="Search categories…"
              />
            </div>
            <div className="w-full max-w-xs">
              <Label className="mb-1.5 block">
                Level{" "}
                <span className="font-normal text-muted-foreground">
                  (every level, if none picked)
                </span>
              </Label>
              {/*
                `null` stays "all levels" — the full per-level breakdown
                below. Picking one narrows the report, the shortfall list and
                the drill-down to that level alone (WI-317).
              */}
              <EntityCombobox
                entity="role-levels"
                value={levelId}
                onChange={setLevelId}
                includeNone
                noneLabel="All levels"
                placeholder="All levels"
                searchPlaceholder="Search levels…"
              />
            </div>
          </div>
          {closedCycles.length > 0 && (
            <div>
              <Label className="mb-1.5 block">
                Cycles{" "}
                <span className="font-normal text-muted-foreground">
                  (every closed cycle, if none are picked)
                </span>
              </Label>
              <div className="flex flex-wrap gap-3">
                {closedCycles.map((cycle) => {
                  const checked = selectedCycleIds.includes(cycle.id);
                  return (
                    <Label key={cycle.id} className="flex items-center gap-2 font-normal text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(next) =>
                          setSelectedCycleIds((ids) =>
                            next === true
                              ? [...ids, cycle.id]
                              : ids.filter((id) => id !== cycle.id),
                          )
                        }
                      />
                      {cycle.name}
                    </Label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DetailSection>

      {!roleId ? (
        <AllRolesOverview
          cycleIds={selectedCycleIds}
          categoryId={categoryId}
          levelId={levelId}
          onSelectRole={setRoleId}
        />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load the report"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !groups || groups.length === 0 ? (
        <EmptyState
          title="Nothing to report yet"
          description={
            categoryId || levelId
              ? "No closed-cycle reviews for this role meet the minimum group size on a competency in this selection. Clear the category or level filter to see the rest."
              : "No closed-cycle reviews meet the minimum group size for this role."
          }
        />
      ) : (
        groups.map((group) => (
          <DetailSection key={group.roleLevelId ?? "all"} title={group.roleLevelLabel}>
            <ul className="divide-y divide-border rounded-lg border border-border">
              {group.competencies.map((row) => (
                <li key={row.competencyId}>
                  <button
                    type="button"
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-4 p-3 text-left hover:bg-muted/40"
                    onClick={() =>
                      setDrillDown({
                        roleLevelId: group.roleLevelId,
                        roleLevelLabel: group.roleLevelLabel,
                        competency: row,
                      })
                    }
                  >
                    <span className="flex items-center gap-1.5 truncate font-medium text-sm">
                      {categoryColorVar(row.categoryColor) && (
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: categoryColorVar(row.categoryColor) ?? undefined,
                          }}
                          aria-hidden
                        />
                      )}
                      {row.competencyName}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {row.population} {row.population === 1 ? "person" : "people"}
                    </span>
                    <span className="font-medium text-sm">{row.averageOrdinal.toFixed(1)}</span>
                    <VarianceBadge row={row} />
                  </button>
                </li>
              ))}
            </ul>
          </DetailSection>
        ))
      )}

      {drillDown && (
        <ContributorsDialog
          roleId={roleId}
          cycleIds={selectedCycleIds}
          drillDown={drillDown}
          onOpenChange={(open) => {
            if (!open) setDrillDown(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The comparison entry point — WI-319. Kori's stated purpose for the whole
 * report is targeting enablement, so this is what loads before any role is
 * picked: one bar per role, worst first, so a shortfall is spotted without
 * opening twenty-five reports in turn. Selecting a bar opens that role's
 * existing breakdown via `onSelectRole`; the role dropdown above still works
 * as a direct jump.
 */
function AllRolesOverview({
  cycleIds,
  categoryId,
  levelId,
  onSelectRole,
}: {
  cycleIds: string[];
  categoryId: string | null;
  levelId: string | null;
  onSelectRole: (roleId: string) => void;
}) {
  const {
    data: rows,
    isLoading,
    isError,
    error,
    refetch,
  } = useAllRolesCompetencyOverview(cycleIds, categoryId, levelId);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load the role comparison"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title="Nothing to compare yet"
        description="No role has enough closed-cycle reviews yet. Choose a role above once one does."
      />
    );
  }

  const items: BarItem[] = rows.map((r) => ({
    key: r.roleId,
    label: r.roleName,
    value: r.averageOrdinal ?? 0,
    note: r.meetsMinimum ? undefined : "Too few responses",
  }));

  return (
    <DetailSection title="Every role, at a glance">
      <BarList
        items={items}
        title="Average competency score by role"
        max={SCALE_MAX}
        formatValue={(n) => n.toFixed(1)}
        onSelect={(item) => onSelectRole(item.key ?? "")}
      />
    </DetailSection>
  );
}

function VarianceBadge({ row }: { row: CompetencyAggregateRow }) {
  if (!row.hasExpectation || row.variance === null) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  const rounded = Math.round(row.variance * 10) / 10;
  const label = rounded === 0 ? "On expectation" : rounded > 0 ? `+${rounded}` : `${rounded}`;
  const tone = rounded < 0 ? "warning" : rounded > 0 ? "success" : "neutral";
  return <StatusPill tone={tone} label={label} />;
}

function ContributorsDialog({
  roleId,
  cycleIds,
  drillDown,
  onOpenChange,
}: {
  roleId: string | null;
  cycleIds: string[];
  drillDown: {
    roleLevelId: string | null;
    roleLevelLabel: string;
    competency: CompetencyAggregateRow;
  };
  onOpenChange: (open: boolean) => void;
}) {
  const { data: scope } = useCompetencyReportScope();
  const { data, isLoading, isError, error, refetch } = useCompetencyAggregateContributors(
    roleId,
    drillDown.roleLevelId,
    drillDown.competency.competencyId,
    cycleIds,
    true,
  );

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{drillDown.competency.competencyName}</DialogTitle>
          <DialogDescription>
            {drillDown.roleLevelLabel} · each person's own averaged score for this competency.
          </DialogDescription>
        </DialogHeader>
        {isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Couldn't load contributors"}
            onRetry={() => refetch()}
          />
        ) : isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <ul className="max-h-80 space-y-1 overflow-y-auto">
            {scope?.mode === "pillar" && (
              <li className="px-2 pb-2 text-muted-foreground text-sm">
                The figure covers everyone in the role. You see names only for people who report to
                you{(data ?? []).length === 0 ? " — none of them are in this one" : ""}.
              </li>
            )}
            {(data ?? []).map((c) => (
              <li key={c.personId}>
                <Link
                  to="/dashboard/persons/$personId"
                  params={{ personId: c.personId }}
                  className="flex items-center justify-between gap-4 rounded-md px-2 py-1.5 text-sm hover:bg-muted/40 hover:underline"
                >
                  <span>{c.personName}</span>
                  <span className="font-medium">{c.averageOrdinal.toFixed(1)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
